import { useState } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
} from '@chakra-ui/react';
import { Text, Button } from '@mercator-gcp/grout';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import HideShowPasswordButton from 'components/elements/Button/HideShowPassword';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import BasePageWithTopNav from 'components/layout/TopNavigationLayout';
import PageMetadata from 'components/PageMetadata';
import { usePasswordReset } from 'hooks/usePasswordReset';
import { trackChangePassword } from 'utils/analytics';
import { ChangePasswordClickActions } from 'utils/analytics/constants';

const SUBMIT_BUTTON_TEXT = 'Reset password';

type PasswordResetFormValues = {
  password: string;
  confirmPassword: string;
};

const styles = {
  heading: {
    mb: 'padding_16',
    flexWrap: 'wrap',
    gap: 'padding_8',
  },
  buttonContainer: {
    width: '100%',
    justifyContent: 'center',
  },
  formControl: {
    mb: '20px',
  },
  inputGroupButton: {
    mr: 'padding_8',
  },
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    mt: '50px',
  },
  box: {
    maxWidth: '400px',
    width: '100%',
  },
};

const PasswordReset = () => {
  const { resetToken } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { mutate, isLoading } = usePasswordReset();
  const {
    register,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
  } = useForm<PasswordResetFormValues>();

  const passwordValue = watch('password');

  const onSubmit = (data: PasswordResetFormValues) => {
    mutate({ resetToken: resetToken!, password: data.password });
    trackChangePassword({
      clickAction: ChangePasswordClickActions.SUBMIT,
      clickText: SUBMIT_BUTTON_TEXT,
    });
  };

  return (
    <BasePageWithTopNav>
      <PageMetadata title="Password Reset" />
      <FlexColumn>
        <FlexRow sx={styles.container}>
          <Box sx={styles.box}>
            <FlexRow sx={styles.heading}>
              <Text.H3>Reset your password</Text.H3>
              <Text>Create a new password for your account</Text>
            </FlexRow>

            <form onSubmit={handleSubmit(onSubmit)}>
              <FormControl isInvalid={!!errors.password} sx={styles.formControl}>
                <FormLabel>Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters long',
                      },
                      validate: () =>
                        getValues('password') === getValues('confirmPassword') ||
                        'Passwords do not match',
                    })}
                    placeholder="Enter your new password"
                  />
                  <InputRightElement>
                    <HideShowPasswordButton
                      isPasswordVisible={showPassword}
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>
                  {errors.password && errors.password.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.confirmPassword} sx={styles.formControl}>
                <FormLabel>Confirm Password</FormLabel>
                <InputGroup>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === getValues('password') || 'Passwords do not match',
                    })}
                    placeholder="Confirm your new password"
                  />
                  <InputRightElement>
                    <HideShowPasswordButton
                      isPasswordVisible={showConfirmPassword}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>
                  {errors.confirmPassword && errors.confirmPassword.message}
                </FormErrorMessage>
              </FormControl>

              <FlexRow sx={styles.buttonContainer}>
                <Button
                  variant="primary"
                  type="submit"
                  isLoading={isLoading}
                  isDisabled={isLoading || !passwordValue}
                >
                  {SUBMIT_BUTTON_TEXT}
                </Button>
              </FlexRow>
            </form>
          </Box>
        </FlexRow>
      </FlexColumn>
    </BasePageWithTopNav>
  );
};

export default PasswordReset;
